import React, { useRef, useState } from 'react';

import { AxiosError } from 'axios';
import { isEmpty } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import * as magicImportApi from 'api/magicImport';
import { IDropZoneFiles } from 'components/shared/Assets/shared/Form/types';
import { SNACKBAR_SKIN } from 'components/shared/Snackbar';
import Text from 'components/shared/Typography/Text';
import { showSnackbar } from 'hooks/useSnackbar';
import { magicImportTripEventsAction } from 'store/itinerariesItem';
import { itinerariesItemDataSelector, itineraryScheduleTypeSelector } from 'store/itinerariesItem/selectors';
import { setMovingBlocked } from 'store/movingBlocked';

import s from './index.module.css';
import { magicImportModalManager } from './MagicImportModals/MagicImportStatusModal/modalManager';

const MONITORING_INTERVAL = 2_000; // 2 Sec
const MONITORING_TIMEOUT = 600_000; // 10 Min
const SNACKBAR_ID = 'MAGIC_IMPORT';

const closeSnackbar = (uniqueId: string) => {
  const snackbarElement = document.getElementById(uniqueId);
  if (snackbarElement) {
    snackbarElement.remove();
  }
};

const isCanceledError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    return error.code === 'ERR_CANCELED';
  }
  if (error instanceof Error) {
    return error.name === 'CanceledError' || error.name === 'AbortError';
  }
  return false;
};

export const useMagicImportLogic = () => {
  const dispatch = useDispatch();
  const scheduleType = useSelector(itineraryScheduleTypeSelector);
  const [status, setStatus] = useState<'idle' | 'uploading' | 'monitoring' | 'gettingResult'>('idle');
  const abortRef = useRef<AbortController | null>(null);
  const itinerary = useSelector(itinerariesItemDataSelector);

  const cleanup = () => {
    dispatch(setMovingBlocked(false));
    setStatus('idle');
    abortRef.current?.abort();
    abortRef.current = null;
  };

  const magicImport = async (file: IDropZoneFiles, onUploadComplete?: () => void) => {
    try {
      setStatus('uploading');

      const response = await magicImportApi.uploadFile(file, 'Itinerary', itinerary.id);

      onUploadComplete?.();

      magicImportModalManager.showProcessingModal();
      await monitoringAndGettingResult(response.id, onUploadComplete);
    } catch (e) {
      console.error('Magic import upload error:', e);
      onUploadComplete?.();
      cleanup();
      showSnackbar({
        text: (
          <div>
            <Text type="brandon" color="white">
              Magic import failed to upload.
            </Text>
            <Text
              type="brandon"
              color="white"
              size="regular"
              onClick={() => {
                closeSnackbar(SNACKBAR_ID + '-error');
                magicImport(file, onUploadComplete);
              }}
              className={s.retryButton}
            >
              Retry
            </Text>
          </div>
        ),
        uniqueId: SNACKBAR_ID + '-error',
        type: SNACKBAR_SKIN.ERROR,
      });
    }
  };

  const monitoringAndGettingResult = async (id: number, onUploadComplete?: () => void) => {
    try {
      dispatch(setMovingBlocked(true));
      setStatus('monitoring');
      abortRef.current = new AbortController();

      const response = await monitorStatus(id, abortRef.current);
      if (response.status !== 'completed') throw new Error('Magic import failed after monitoring');
      setStatus('gettingResult');
      if (!response?.data || !Array.isArray(response.data)) throw new Error('Failed to get magic import result');

      await dispatch(
        magicImportTripEventsAction({
          importEvents: response.data,
          itineraryOrganizedBy: scheduleType,
        }),
      );

      magicImportModalManager.closeModal();
      magicImportModalManager.showCompletedModal(id);
    } catch (e) {
      if (isCanceledError(e)) {
        return;
      }

      console.error('Magic import monitoring error:', e);
      magicImportModalManager.closeModal();
      showSnackbar({
        text: (
          <div>
            <Text type="brandon" color="white">
              Magic import failed.
            </Text>
            <Text
              type="brandon"
              color="white"
              size="regular"
              onClick={() => {
                closeSnackbar(SNACKBAR_ID + '-error');
                magicImportModalManager.showProcessingModal();
                retryImport(id, onUploadComplete);
              }}
              className={s.retryButton}
            >
              Retry
            </Text>
          </div>
        ),
        uniqueId: SNACKBAR_ID + '-error',
        type: SNACKBAR_SKIN.ERROR,
      });
    } finally {
      cleanup();
    }
  };

  const retryImport = async (id: number, onUploadComplete?: () => void) => {
    try {
      await magicImportApi.retryImport(id);
      monitoringAndGettingResult(id, onUploadComplete);
    } catch (e) {
      console.error('Magic import retry error:', e);
      onUploadComplete?.();
      magicImportModalManager.closeModal();
      cleanup();
      showSnackbar({
        text: 'Magic import failed to retry',
        uniqueId: SNACKBAR_ID + '-error',
        type: SNACKBAR_SKIN.ERROR,
      });
    }
  };

  const magicImportInProgress = ['processing', 'pending'].includes(itinerary?.magicImport?.status) && status === 'idle';
  const magicImportCompleted =
    itinerary?.magicImport?.status === 'completed' && !isEmpty(itinerary.magicImport.result) && status === 'idle';

  React.useEffect(() => {
    try {
      if (magicImportInProgress) monitoringAndGettingResult(itinerary.magicImport.id);

      if (magicImportCompleted) {
        dispatch(
          magicImportTripEventsAction({
            importEvents: itinerary.magicImport.result,
            itineraryOrganizedBy: scheduleType,
          }),
        );
      }
    } catch (e) {
      console.error('Magic import status check error:', e);
      showSnackbar({
        text: 'Failed to check magic import status',
        uniqueId: SNACKBAR_ID + '-status-error',
        type: SNACKBAR_SKIN.ERROR,
      });
    }

    return () => {
      cleanup();
      closeSnackbar(SNACKBAR_ID + '-error');
      closeSnackbar(SNACKBAR_ID + '-status-error');
    };
  }, [itinerary?.magicImport?.status, itinerary?.magicImport?.result]);

  const isLoading = status !== 'idle';

  return { status, isLoading, magicImport };
};

export const monitorStatus = async (
  id: number,
  abortController: AbortController,
): Promise<{ data: magicImportApi.TripEvent[]; status: 'pending' | 'processing' | 'completed' | 'failed' }> => {
  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      try {
        const response = await magicImportApi.checkStatus(id, abortController);
        if (response.status === 'processing' || response.status === 'pending') return;

        clearInterval(interval);
        clearTimeout(timeout);

        if (response.status === 'completed') {
          resolve(response);
        } else {
          reject(response.status);
        }
      } catch (error) {
        console.error('Monitor status error:', error);

        clearInterval(interval);
        clearTimeout(timeout);

        if (isCanceledError(error)) {
          return;
        }

        reject(error);
      }
    }, MONITORING_INTERVAL);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      reject('timeout');
    }, MONITORING_TIMEOUT);

    abortController.signal.addEventListener('abort', () => {
      clearInterval(interval);
      clearTimeout(timeout);
    });
  });
};
